import { Link } from 'react-router-dom';
import { site } from '../config/site';

export type LegalKind = 'privacy' | 'terms' | 'support';

const META: Record<
  LegalKind,
  { title: string; heading: string; description: string }
> = {
  privacy: {
    title: 'Privacy Policy · Gifty',
    heading: 'Privacy Policy',
    description: 'How Gifty collects and uses your information.',
  },
  terms: {
    title: 'Terms of Use · Gifty',
    heading: 'Terms of Use',
    description: 'Terms for using the Gifty gift-ideas and reminder app.',
  },
  support: {
    title: 'Support · Gifty',
    heading: 'Support',
    description: 'Help with Gifty subscriptions, shortlists, and account.',
  },
};

function PrivacyBody() {
  return (
    <>
      <h2>Who we are</h2>
      <p>
        Gifty is an AI gift-ideas and reminder app operated by{' '}
        <strong>Omnivatis</strong> (“we”, “us”). Contact:{' '}
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>
          <strong>Contact info</strong> — email and name for your account.
        </li>
        <li>
          <strong>User content</strong> — the people you add (name, relationship,
          interests, notes), the occasions and dates you record, the gifts you log,
          and the AI shortlists generated for you. Gifty does not use your camera
          and does not collect photos.
          check-offs and reminder times.
        </li>
        <li>
          <strong>Purchases</strong> — subscription product and entitlement
          dates from Apple App Store / Google Play (we do not store full payment
          card numbers).
        </li>
        <li>
          <strong>Identifiers</strong> — account ID; device / push tokens for
          Firebase Cloud Messaging and analytics.
        </li>
        <li>
          <strong>Usage data</strong> — product events (sign-in, paywall,
          purchase/restore, shortlist generated, quota reached) via Firebase
          Analytics.
        </li>
        <li>
          <strong>Diagnostics</strong> — crash data via Firebase Crashlytics.
        </li>
      </ul>

      <h2>How we use information</h2>
      <ul>
        <li>
          <strong>App functionality</strong> — authenticate you, store the people
          and gift history you record, generate gift shortlists, send occasion
          reminders, and enforce subscription shortlist quotas.
        </li>
        <li>
          <strong>Analytics &amp; reliability</strong> — understand feature use
          and fix crashes.
        </li>
      </ul>

      <h2>AI processing</h2>
      <p>
        Photos and related prompts may be sent to our AI providers (currently
        OpenAI) to produce a structured list of gift ideas.
        Outputs are wellness guidance only — not a medical diagnosis. Avoid
        uploading images of others without permission.
      </p>

      <h2>Sharing</h2>
      <p>
        We do not sell your personal information. We share data with processors
        needed to run Gifty (hosting, S3 storage, AI, analytics, push, app
        stores) under appropriate agreements.
      </p>

      <h2>Retention &amp; deletion</h2>
      <p>
        We keep your account, people and gift history while your account is active. You can
        delete products or your account from Profile. Store subscriptions must
        be canceled separately in Apple / Google settings.
      </p>

      <h2>Children</h2>
      <p>
        Gifty is not directed to children under 13 (or the minimum age
        required in your region).
      </p>

      <h2>Changes</h2>
      <p>
        We may update this policy. The “Last updated” date above will change when
        we do.
      </p>
      <p className="cta-row">
        <Link className="cta" to={site.termsUrl}>
          Terms of Use
        </Link>
        <Link className="cta" to={site.supportUrl}>
          Support
        </Link>
      </p>
    </>
  );
}

function TermsBody() {
  return (
    <>
      <h2>Agreement</h2>
      <p>
        By downloading or using Gifty you agree to these Terms of Use and
        our <Link to={site.privacyUrl}>Privacy Policy</Link>. If you do not
        agree, do not use the app.
      </p>
      <h2>The service</h2>
      <p>
        Gifty provides AI-assisted gift suggestions, a record of the people you
        buy for and what you have given them, and occasion reminders. Content is for personal
        wellness and habit support — <strong>not medical diagnosis</strong>,
        treatment, or prescription advice.
      </p>
      <h2>Accounts &amp; hard paywall</h2>
      <p>
        Access to the main app requires an active subscription purchased before
        or bound after signup. You must provide accurate registration details
        and keep credentials secure.
      </p>
      <h2>Subscriptions &amp; shortlist limits</h2>
      <ul>
        <li>
          Plans: Weekly (10 gift shortlists), Monthly (40), Yearly (600),
          billed through Apple App Store or Google Play.
        </li>
        <li>
          Each AI gift shortlist you generate counts as one against the current
          billing period. Re-opening a saved shortlist is free.
        </li>
        <li>
          Payment is charged at confirmation of purchase; subscriptions renew
          unless canceled at least 24 hours before period end.
        </li>
        <li>
          Manage or cancel in store account settings. Deleting the app or your
          Gifty account does not cancel a store subscription.
        </li>
      </ul>
      <h2>Acceptable use</h2>
      <p>
        Do not misuse Gifty — including unlawful activity, uploading
        others’ photos without consent, or attempting to disrupt the service.
      </p>
      <h2>AI outputs</h2>
      <p>
        Analysis is automated and may be imperfect. Use your judgment; Gifty
        AI is not liable for decisions made solely on AI output.
      </p>
      <h2>Disclaimer &amp; liability</h2>
      <p>
        The service is provided “as is.” To the fullest extent permitted by law,
        Omnivatis is not liable for indirect or consequential damages arising
        from your use of Gifty.
      </p>
      <h2>Contact</h2>
      <p>
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>
      </p>
      <p className="cta-row">
        <Link className="cta" to={site.privacyUrl}>
          Privacy Policy
        </Link>
        <Link className="cta" to={site.supportUrl}>
          Support
        </Link>
      </p>
    </>
  );
}

function SupportBody() {
  return (
    <>
      <p>
        Need help with Gifty? Email{' '}
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>
      <div className="support-topic">
        <strong>Subscription required</strong>
        <span>
          There is no free path into the main app. Subscribe or Restore
          purchases on the paywall, then create or sign into your account.
        </span>
      </div>
      <div className="support-topic">
        <strong>Subscription not unlocking</strong>
        <span>
          Use Restore purchases on the paywall. Confirm you are signed into the
          same Apple / Google account used to buy.
        </span>
      </div>
      <div className="support-topic">
        <strong>Out of shortlists</strong>
        <span>
          Generating a shortlist counts toward your plan. Your people, occasions
          and gift history stay available; upgrade or wait for the period reset
          for more shortlists.
        </span>
      </div>
      <div className="support-topic">
        <strong>Cancel or change plan</strong>
        <span>
          Manage subscriptions in App Store or Google Play account settings.
        </span>
      </div>
      <div className="support-topic">
        <strong>Delete account</strong>
        <span>
          In the app: Profile → Delete account. Or email us from your registered
          address.
        </span>
      </div>
      <h2>Legal</h2>
      <p className="cta-row">
        <Link className="cta" to={site.privacyUrl}>
          Privacy Policy
        </Link>
        <Link className="cta" to={site.termsUrl}>
          Terms of Use
        </Link>
      </p>
    </>
  );
}

export function LegalPage({ kind }: { kind: LegalKind }) {
  const meta = META[kind];

  return (
    <main className="legal-wrap">
      <div className="page-intro">
        <div className="eyebrow">Gifty</div>
        <h1>{meta.heading}</h1>
        <p className="updated">Last updated: August 1, 2026</p>
      </div>
      <article className="legal-article">
        {kind === 'privacy' && <PrivacyBody />}
        {kind === 'terms' && <TermsBody />}
        {kind === 'support' && <SupportBody />}
      </article>
    </main>
  );
}

export function legalDocumentTitle(kind: LegalKind): string {
  return META[kind].title;
}
